import { Menu as BaseMenu } from '@base-ui/react/menu';
import { FC, ReactNode } from 'react';

import {
  StyledGroupLabel,
  StyledItem,
  StyledItemDescription,
  StyledItemIcon,
  StyledItemLabel,
  StyledItemText,
  StyledItemTrailing,
  StyledPopup,
  StyledSeparator,
} from './Menu.styled';
import type { MenuActionItem, MenuItem, MenuProps } from './types';

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M13.3 4.2L6.2 11.3L2.7 7.8L3.6 6.9L6.2 9.5L12.4 3.3L13.3 4.2Z" fill="currentColor" />
  </svg>
);

type Segment =
  | { kind: 'separator'; key: string }
  | { kind: 'group'; key: string; label?: string; items: MenuActionItem[] };

/**
 * Splits a flat item list into render segments.
 *
 * Base UI requires `Menu.GroupLabel` to live inside a `Menu.Group`; rendering one
 * standalone throws. So a `label` item opens a labelled group that absorbs every
 * action after it until the next label or separator. Runs of actions with no label
 * are emitted as an unlabelled group, which needs no `Menu.Group` wrapper at all.
 */
const buildSegments = (items: MenuItem[]): Segment[] => {
  const segments: Segment[] = [];
  let current: Extract<Segment, { kind: 'group' }> | null = null;

  const flush = () => {
    if (current && (current.items.length > 0 || current.label)) segments.push(current);
    current = null;
  };

  items.forEach((item, index) => {
    if (item.type === 'separator') {
      flush();
      segments.push({ kind: 'separator', key: item.id ?? `separator-${index}` });
      return;
    }

    if (item.type === 'label') {
      flush();
      current = { kind: 'group', key: item.id ?? `group-${index}`, label: item.label, items: [] };
      return;
    }

    if (!current) current = { kind: 'group', key: `group-${index}`, items: [] };
    current.items.push(item);
  });

  flush();
  return segments;
};

const renderItem = (item: MenuActionItem): ReactNode => {
  const hasDescription = Boolean(item.description);
  const trailing = item.selected ? <CheckIcon /> : item.shortcut;

  return (
    <StyledItem
      key={item.id}
      data-menu-item={item.id}
      $tone={item.tone ?? 'default'}
      $hasDescription={hasDescription}
      disabled={item.disabled}
      onClick={item.onSelect}
    >
      {item.icon && <StyledItemIcon aria-hidden="true">{item.icon}</StyledItemIcon>}

      <StyledItemText>
        <StyledItemLabel>{item.label}</StyledItemLabel>
        {hasDescription && <StyledItemDescription>{item.description}</StyledItemDescription>}
      </StyledItemText>

      {trailing && <StyledItemTrailing>{trailing}</StyledItemTrailing>}
    </StyledItem>
  );
};

/**
 * Command menu for row and toolbar actions.
 *
 * Built on Base UI's Menu, which supplies the parts that are expensive to get right
 * by hand: portalling out of clipping ancestors, roving focus, type-ahead, Escape
 * and outside-press dismissal, focus restoration to the trigger, and the
 * `menu`/`menuitem` ARIA wiring. This component owns the visual language only.
 *
 * Not a form control — use `Select` when the value is part of a form.
 */
const Menu: FC<MenuProps> = ({
  trigger,
  items,
  ariaLabel,
  side = 'bottom',
  align = 'end',
  sideOffset = 6,
  open,
  onOpenChange,
  minWidth = 200,
  className,
}) => (
  <BaseMenu.Root open={open} onOpenChange={onOpenChange}>
    <BaseMenu.Trigger render={trigger} />

    <BaseMenu.Portal>
      <BaseMenu.Positioner side={side} align={align} sideOffset={sideOffset}>
        <StyledPopup $minWidth={minWidth} aria-label={ariaLabel} className={className}>
          {buildSegments(items).map((segment) => {
            if (segment.kind === 'separator') return <StyledSeparator key={segment.key} />;

            if (!segment.label) {
              return segment.items.map(renderItem);
            }

            return (
              <BaseMenu.Group key={segment.key}>
                <StyledGroupLabel>{segment.label}</StyledGroupLabel>
                {segment.items.map(renderItem)}
              </BaseMenu.Group>
            );
          })}
        </StyledPopup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  </BaseMenu.Root>
);

Menu.displayName = 'Menu';

export default Menu;
